/**
 * Premium "Partner Card" — the hero scene of the Partner-Welcome reel.
 *
 * Visual design:
 *  - Centred premium card (rounded glass-morph with brand-tinted border glow).
 *  - Top half: square partner-logo tile with subtle frame + drop shadow.
 *  - Below: partner name (huge), city (subtle), category pill, status dot pill.
 *  - The card enters with a 3D flip from the right, settles, then slowly
 *    floats while the background blobs drift.
 *  - "ALLYVER PARTNER" eyebrow above the card sells the brand context.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  pickMediaSrc, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

export const partnerCardFlip: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const name = inputs.text ?? "PARTNER";
  const city = inputs.subtext ?? "";
  const category = String(inputs.extras?.category ?? "").trim();
  const kategorieType = String(inputs.extras?.kategorieType ?? "").trim();
  const status = String(inputs.extras?.status ?? "active").toLowerCase();
  const statusOn = status === "active";
  const logoUrl = pickMediaSrc(inputs.media, 0);

  // Card geometry.
  const cardW = isVertical ? 820 : 640;
  const cardH = isVertical ? 1080 : 780;
  const logoTileH = isVertical ? 460 : 360;
  const nameSize = typeSize(isVertical ? "mega" : "hero", ctx.format) * 0.62;
  const citySize = typeSize("md", ctx.format) * 0.7;
  const eyeSize = typeSize("sm", ctx.format) * 0.65;
  const pillSize = typeSize("sm", ctx.format) * 0.55;

  const t = makeTl(clipStartSec);
  const eyeId = `${clipPrefix}-eye`;
  const cardId = `${clipPrefix}-card`;
  const pulseId = `${clipPrefix}-pulse`;
  const cardGlowId = `${clipPrefix}-card-glow`;
  const logoTileId = `${clipPrefix}-logo`;
  const logoImgId = `${clipPrefix}-logo-img`;
  const nameId = `${clipPrefix}-name`;
  const cityId = `${clipPrefix}-city`;
  const catId = `${clipPrefix}-cat`;
  const statId = `${clipPrefix}-stat`;
  const statDotId = `${clipPrefix}-stat-dot`;
  const ringId = `${clipPrefix}-ring`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "studio", accent });

  const logoTileInner = logoUrl
    ? `<img id="${logoImgId}" src="${escapeHtml(logoUrl)}" alt="${escapeHtml(name)}" style="max-width:95%;max-height:95%;width:auto;height:auto;object-fit:contain;border-radius:20px;filter:drop-shadow(0 12px 30px rgba(0,0,0,0.35));" />`
    : `<div id="${logoImgId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${nameSize * 0.6}px;letter-spacing:-2px;color:${themeColors.fg};">${escapeHtml(name.charAt(0))}</div>`;

  const categoryPill = category
    ? `<div id="${catId}" style="display:inline-flex;align-items:center;gap:10px;padding:14px 24px;border-radius:9999px;background:${withAlpha(accent, 0.18)};border:1.5px solid ${withAlpha(accent, 0.55)};box-shadow:0 0 24px ${withAlpha(accent, 0.25)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${pillSize}px;letter-spacing:2.5px;text-transform:uppercase;color:${accent};">
        ${kategorieType ? `<span style="opacity:0.7;">${escapeHtml(kategorieType)}</span><span style="opacity:0.5;">·</span>` : ""}
        <span>${escapeHtml(category)}</span>
      </div>`
    : "";

  const statusPill = `<div id="${statId}" style="display:inline-flex;align-items:center;gap:10px;padding:12px 22px;border-radius:9999px;background:${statusOn ? withAlpha("#22c55e", 0.15) : withAlpha(themeColors.fg, 0.07)};border:1.5px solid ${statusOn ? withAlpha("#22c55e", 0.55) : withAlpha(themeColors.fg, 0.18)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${pillSize}px;letter-spacing:2px;text-transform:uppercase;color:${statusOn ? "#4ade80" : withAlpha(themeColors.fg, 0.7)};">
      <span id="${statDotId}" style="width:10px;height:10px;border-radius:9999px;background:${statusOn ? "#22c55e" : withAlpha(themeColors.fg, 0.4)};box-shadow:${statusOn ? `0 0 12px #22c55e` : "none"};"></span>
      <span>${statusOn ? "LIVE" : "BALD VERFÜGBAR"}</span>
    </div>`;

  const html = `<div id="${clipPrefix}-pad" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:36px;padding:80px;perspective:1600px;">
    <div id="${eyeId}" style="display:flex;align-items:center;gap:14px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyeSize}px;letter-spacing:7px;text-transform:uppercase;color:${accent};opacity:0;">
      <span style="width:36px;height:2px;background:${accent};"></span>
      <span>Allyver Partner</span>
      <span style="width:36px;height:2px;background:${accent};"></span>
    </div>

    <div id="${cardId}" style="position:relative;transform-style:preserve-3d;">
      <div id="${pulseId}" style="position:relative;width:${cardW}px;height:${cardH}px;border-radius:48px;background:linear-gradient(165deg, rgba(20,24,38,0.55) 0%, rgba(8,10,18,0.55) 100%);box-shadow:0 40px 100px rgba(0,0,0,0.55), inset 0 0 0 1.5px ${withAlpha(themeColors.fg, 0.16)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.18)};display:flex;flex-direction:column;align-items:center;justify-content:flex-start;padding:48px 48px 40px;gap:18px;transform-style:flat;will-change:transform;backface-visibility:hidden;-webkit-backface-visibility:hidden;isolation:isolate;">

        <div id="${cardGlowId}" style="position:absolute;inset:-2px;border-radius:50px;background:linear-gradient(135deg, ${withAlpha(accent, 0.7)} 0%, transparent 40%, transparent 60%, ${withAlpha(accent, 0.55)} 100%);opacity:0;pointer-events:none;mix-blend-mode:screen;filter:blur(1px);"></div>

        <div id="${ringId}" style="position:absolute;top:-14px;right:-14px;padding:14px 22px;border-radius:9999px;background:${accent};box-shadow:0 0 40px ${withAlpha(accent, 0.6)}, 0 4px 20px rgba(0,0,0,0.4);opacity:0;display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${pillSize * 1.3}px;letter-spacing:3px;text-transform:uppercase;color:${themeColors.bg};">NEU</div>

        <div id="${logoTileId}" style="width:100%;height:${logoTileH}px;border-radius:32px;background:transparent;display:flex;align-items:center;justify-content:center;padding:16px;overflow:hidden;">
          ${logoTileInner}
        </div>

        <div id="${nameId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${nameSize}px;line-height:0.96;letter-spacing:-2.5px;color:${themeColors.fg};text-align:center;text-wrap:balance;">${escapeHtml(name)}</div>

        ${city ? `<div id="${cityId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${citySize}px;letter-spacing:0.3px;color:${withAlpha(themeColors.fg, 0.7)};display:flex;align-items:center;gap:8px;">
          <svg width="${Math.round(citySize * 0.9)}" height="${Math.round(citySize * 0.9)}" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
          <span>${escapeHtml(city)}</span>
        </div>` : ""}

        <div style="display:flex;flex-direction:column;align-items:center;gap:14px;">
          ${categoryPill}
          ${statusPill}
        </div>
      </div>
    </div>
  </div>`;

  const init = [
    `gsap.set("#${eyeId}", { opacity: 0, y: -16 });`,
    // Outer card: nur Flip + Opacity. Skalierung NICHT hier — die übernimmt
    // der Inner `pulseId`, sonst kollidiert das mit dem Atmen am Ende.
    `gsap.set("#${cardId}", { opacity: 0, rotationY: -75, transformOrigin: "50% 50%" });`,
    // Inner pulse-wrapper startet 8% kleiner, expandiert auf 1 und atmet dann
    // kontinuierlich. transformOrigin im Zentrum, damit der Zoom symmetrisch
    // wirkt (kein Drift). force3D erzwingt eine eigene GPU-Compositing-Schicht,
    // sodass der Zoom-Pulse stutter-frei und ohne Sub-Pixel-Border-Artefakte
    // (weiße Säume) gerendert wird.
    `gsap.set("#${pulseId}", { scale: 0.92, y: 0, transformOrigin: "50% 50%", force3D: true });`,
    `gsap.set("#${logoTileId}", { scale: 0.92 });`,
    `gsap.set("#${nameId}", { opacity: 0, y: 18 });`,
    city ? `gsap.set("#${cityId}", { opacity: 0, y: 14 });` : "",
    category ? `gsap.set("#${catId}", { opacity: 0, y: 12, scale: 0.9 });` : "",
    `gsap.set("#${statId}", { opacity: 0, y: 12, scale: 0.9 });`,
    `gsap.set("#${ringId}", { opacity: 0, scale: 0.4 });`,
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    // Outer: Flip-In (rotationY + opacity), kein scale auf dem Outer.
    t.to(`#${cardId}`, { opacity: 1, rotationY: 0, duration: 1.0, ease: "raw:\"power3.out\"" }, 0.05),
    // Inner pulse-wrapper expandiert auf 1.0 (overshoot durch back.out).
    t.to(`#${pulseId}`, { scale: 1, duration: 0.9, ease: "raw:\"back.out(1.6)\"", force3D: true }, 0.2),
    // Glow während des Flips.
    t.fromTo(`#${cardGlowId}`, { opacity: 0 }, { opacity: 0.85, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.2),
    t.to(`#${cardGlowId}`, { opacity: 0.22, duration: 0.7, ease: "raw:\"power2.inOut\"" }, 0.75),
    // Eyebrow.
    t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.7),
    // Logo tile breathes.
    t.to(`#${logoTileId}`, { scale: 1, duration: 0.7, ease: "raw:\"back.out(1.4)\"" }, 0.7),
    // Name + city.
    t.to(`#${nameId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 1.0),
  ];
  if (city) tweens.push(t.to(`#${cityId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.15));
  if (category) tweens.push(t.to(`#${catId}`, { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "raw:\"back.out(1.5)\"" }, 1.3));
  tweens.push(t.to(`#${statId}`, { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "raw:\"back.out(1.5)\"" }, 1.45));
  // Ring stamp pops in last.
  tweens.push(t.to(`#${ringId}`, { opacity: 1, scale: 1, duration: 0.45, ease: "raw:\"back.out(2.4)\"" }, 1.6));

  // Looping pulse: sichtbarer Zoom-In/Out + sanftes Float in EINER Tween auf
  // dem inneren Wrapper. Damit kein Property-Konflikt mit dem Outer (der das
  // statische rotationY:0 als Reststate trägt). Periode 2.4s, sine.inOut →
  // weich und harmonisch. Scale-Amplitude 4% = klar sichtbar, aber nicht
  // hektisch. Float ±10px im selben Rhythmus, sodass Zoom-Up + Lift-Up
  // synchron sind und der Gesamteindruck atmend wirkt.
  const pulseStart = 2.2;
  tweens.push(t.fromTo(
    `#${pulseId}`,
    { scale: 1, y: 0 },
    { scale: 1.035, y: -8, duration: 2.6, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"", force3D: true },
    pulseStart,
  ));
  // Glow folgt demselben Rhythmus → wirkt wie ein „Atmen" inkl. Aura.
  tweens.push(t.to(`#${cardGlowId}`, { opacity: 0.6, duration: 2.4, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, pulseStart));
  // „NEU"-Badge im schnelleren Eigentakt (Aufmerksamkeit).
  tweens.push(t.to(`#${ringId}`, { scale: 1.12, duration: 0.85, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, pulseStart + 0.15));
  // LIVE-Statusdot Herzschlag (nur bei active).
  if (statusOn) {
    tweens.push(t.to(`#${statDotId}`, { scale: 1.55, duration: 0.65, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, pulseStart));
  }

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
